import { useState } from 'react';
import { Box, Card, CardContent, CardHeader, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { FormProvider } from 'react-hook-form';

import { ATTACKIQ_ID } from '@attackiq/constants/platform';

import ContentModules from '../../components/ContentModules';
import TabPanel from '../../components/mui/TabPanel/TabPanel';
import { SetContentLastUpdatedDate } from '../../components/SetContentLastUpdatedDate';
import TaggedItemsEditor from '../../components/TaggedItemsEditor';
import { ProjectTemplate } from '../../types';

import GeneralTab from './components/tabs/GeneralTab/GeneralTab';
import ReportsTab from './components/tabs/ReportsTab/ReportsTab';
import TestsTab from './components/tabs/TestsTab/TestsTab';

const AssessmentTemplateTabs = {
  General: 0,
  Reports: 1,
  Tests: 2,
  Modules: 3
} as const;

const AssessmentTemplateEdit = () => {
  const [tab, setTab] = useState<number>(AssessmentTemplateTabs.General);
  const methods = useForm({
    refineCoreProps: {
      queryOptions: {
        gcTime: 0,
        staleTime: 0
      }
    }
  });
  const {
    refineCore: { formLoading, onFinish, id: projectTemplateId },
    handleSubmit
  } = methods;

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
    setTab(value);
  };

  const onSubmit = handleSubmit(validData => {
    // TODO:
    // Project name and Project description are what the assessments using this template will be called, there is no need for these to exist because we use the template name and description
    // But the serializer needs this at the moment.
    onFinish({
      ...validData,
      project_id: null, // This shouldnt be required
      project_name: validData.template_name,
      project_description: validData.template_description,
      company: ATTACKIQ_ID
    });
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Edit
        isLoading={formLoading}
        footerButtons={({ defaultButtons }) => (
          <>
            <SetContentLastUpdatedDate<ProjectTemplate> resourceNameKey="template_name" />
            {defaultButtons}
          </>
        )}
        saveButtonProps={{
          onClick: onSubmit
        }}
      >
        <FormProvider {...methods}>
          <Paper variant="outlined" square>
            <Tabs value={tab} onChange={handleTabChange} aria-label="assessment template tabs">
              <Tab label="General" />
              <Tab label="Reports" />
              <Tab label="Tests" />
              <Tab label="Modules" />
            </Tabs>

            <TabPanel value={tab} index={AssessmentTemplateTabs.General}>
              <GeneralTab />
            </TabPanel>

            <TabPanel value={tab} index={AssessmentTemplateTabs.Reports}>
              <ReportsTab />
            </TabPanel>

            <TabPanel value={tab} index={AssessmentTemplateTabs.Tests}>
              <TestsTab />
            </TabPanel>

            <TabPanel value={tab} index={AssessmentTemplateTabs.Modules}>
              <ContentModules contentType="projecttemplate" />
            </TabPanel>
          </Paper>
        </FormProvider>
      </Edit>
      <Card elevation={1}>
        <CardHeader title={<Typography variant="h5">Edit Assessment Template Tags</Typography>} />
        <CardContent>
          <TaggedItemsEditor
            objectId={projectTemplateId as string}
            contentType={'projecttemplate'}
            disabled={formLoading}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssessmentTemplateEdit;
